<script setup lang="ts">
import { useClassStudents } from '@/composables/student/useClassStudents';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import GroupIcon from '@/assets/icons/GroupIcon.vue';
import { useDisplayName } from '@/composables/useDisplayName';
import { SearchHighlightTextInjectionKey } from '@/composables/useSearchSearchHighlightText';
import { useStudentSelectionLabel } from '@/composables/useStudentSelectionLabel';
import { useMobile } from '@/composables/useMobile';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SearchTextHighlighter from '@/core/elements/SearchTextHighlighter.vue';
import UserAvatar from '@/core/UserAvatar.vue';
import type { TStudentLike } from '@/interfaces';
import SelectionPopoverDivider from '@/core/selects/SelectionPopoverDivider.vue';
import SelectionPopoverOptionRow from '@/core/selects/SelectionPopoverOptionRow.vue';
import SelectionPopoverSearchHeader from '@/core/selects/SelectionPopoverSearchHeader.vue';
import { focusSelectionSearchOnOpen } from '@/core/selects/selectionPopover.utils';
import { areAllEligibleStudentsSelected, resolveStudentSelection } from '@/utils/students.utils';
import { matchesAllSearchWords } from 'common/utils/string.utils';
import { computed, provide, readonly, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits<{
  'select:all-eligible-students': [];
  'update:students': [students: TStudentLike[]];
  hide: [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement: HTMLElement | null;
    /** Select-all state scoped to eligibleStudents, not necessarily the complete class roster. */
    allEligibleStudentsSelected: boolean;
    /** Batch edits into a local draft and emit `update:students` once on close instead of per toggle. */
    commitOnClose?: boolean;
    disableClickOutside?: boolean;
    eligibleStudents: TStudentLike[];
    mobileDrawer?: boolean;
    selectedStudents: TStudentLike[];
  }>(),
  {
    commitOnClose: false,
    disableClickOutside: false,
    mobileDrawer: false,
  },
);

const { t } = useI18n();
const { currentClass } = useCurrentClass();
const { allStudents } = useClassStudents(() => currentClass.value?.id);
const { getStudentDisplayName } = useDisplayName();
const { isMobile } = useMobile();
const popoverRef = useTemplateRef('popoverRef');
const searchHeaderRef = useTemplateRef('searchHeaderRef');
const searchText = ref('');
provide(SearchHighlightTextInjectionKey, readonly(searchText));

const filteredStudents = computed(() => {
  const query = searchText.value.trim();
  if (!query) return props.eligibleStudents;
  return props.eligibleStudents.filter((student) => matchesAllSearchWords([getStudentDisplayName(student)], query));
});

const showAggregateOption = computed(() => props.eligibleStudents.length > 1);
const singletonAllEligibleSelection = computed(
  () => props.eligibleStudents.length === 1 && props.allEligibleStudentsSelected,
);
const representedStudents = computed(() =>
  resolveStudentSelection({
    allEligibleStudentsSelected: props.allEligibleStudentsSelected,
    eligibleStudents: props.eligibleStudents,
    selectedStudents: props.selectedStudents,
  }),
);
// Keep an empty selection local until a child is picked or the popover closes.
// Some owners normalize [] to the whole class immediately; others persist every update.
const emptySelectionPending = ref(false);
const currentStudents = computed(() => (emptySelectionPending.value ? [] : representedStudents.value));
const selectedStudentIds = computed(() => new Set(currentStudents.value.map((student) => student.id)));

// commitOnClose: edits accumulate in this draft and emit once on close (one setStudents → one activity)
const draftStudents = ref<TStudentLike[]>([]);
// only commit if the popover was actually opened — guards against spurious hide events (e.g. initial mount)
let commitPending = false;
const draftIds = computed<Set<number>>(() => new Set(draftStudents.value.map((student) => student.id)));
const draftAllSelected = computed(() => areAllEligibleStudentsSelected(draftStudents.value, props.eligibleStudents));
const allSelected = computed(() =>
  props.commitOnClose ? draftAllSelected.value : !emptySelectionPending.value && props.allEligibleStudentsSelected,
);

const { label: selectAllLabel } = useStudentSelectionLabel(
  () => props.eligibleStudents,
  allStudents,
  () => t('students.allEligible', { count: props.eligibleStudents.length }),
);

const isSelected = (student: TStudentLike): boolean => {
  return props.commitOnClose ? draftIds.value.has(student.id) : selectedStudentIds.value.has(student.id);
};

const toggleStudent = (student: TStudentLike): void => {
  if (props.commitOnClose) {
    draftStudents.value = draftIds.value.has(student.id)
      ? draftStudents.value.filter((selected) => selected.id !== student.id)
      : [...draftStudents.value, student];
    emptySelectionPending.value = draftStudents.value.length === 0;
    return;
  }
  const selection = currentStudents.value;
  const next = isSelected(student)
    ? selection.filter((selected) => selected.id !== student.id)
    : [...selection, student];
  emptySelectionPending.value = next.length === 0;
  if (!emptySelectionPending.value) emit('update:students', next);
};

const onSelectAllEligible = (): void => {
  if (props.commitOnClose) {
    draftStudents.value = draftAllSelected.value
      ? []
      : resolveStudentSelection({
          allEligibleStudentsSelected: true,
          eligibleStudents: props.eligibleStudents,
          selectedStudents: draftStudents.value,
        });
    emptySelectionPending.value = draftStudents.value.length === 0;
    return;
  }
  emptySelectionPending.value = allSelected.value;
  if (!emptySelectionPending.value) emit('select:all-eligible-students');
};

const show = async (): Promise<void> => {
  searchText.value = '';
  emptySelectionPending.value = false;
  // seed the draft from the current selection each time the popover opens
  if (props.commitOnClose) {
    draftStudents.value = [...representedStudents.value];
    commitPending = true;
  }
  await popoverRef.value?.show();
  await focusSelectionSearchOnOpen(searchHeaderRef.value, isMobile.value);
};

const hide = (): void => popoverRef.value?.hide();

const handleHide = (): void => {
  if (props.commitOnClose && commitPending) {
    commitPending = false;
    emit('update:students', emptySelectionPending.value ? [...props.eligibleStudents] : draftStudents.value);
  } else if (!props.commitOnClose && emptySelectionPending.value) {
    emit('select:all-eligible-students');
  }
  emptySelectionPending.value = false;
  emit('hide');
};

defineExpose({ show, hide, isOpen: computed(() => !!popoverRef.value?.isOpen) });
</script>

<template>
  <PositionablePopover
    ref="popoverRef"
    scroll-mode="content"
    :anchor-element="props.anchorElement"
    class="w-selection-popover"
    :disable-click-outside="props.disableClickOutside"
    flip-strategy="both"
    lazy-content
    :offset-y="10"
    :mobile-drawer-label="$t('students.select')"
    :mobile-drawer-mode="props.mobileDrawer"
    @hide="handleHide"
  >
    <template #default="{ mobileDrawer: isMobileDrawer }">
      <div class="flex min-h-0 min-w-0 w-full flex-1 flex-col" :class="!isMobileDrawer && 'max-h-selection-popover'">
        <SelectionPopoverSearchHeader
          ref="searchHeaderRef"
          v-model="searchText"
          :placeholder="$t('students.selectionSearchPlaceholder')"
        />

        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-smd pb-smd">
          <SelectionPopoverOptionRow
            v-if="showAggregateOption"
            class="shrink-0"
            indicator="checkbox"
            :selected="allSelected"
            @select="onSelectAllEligible"
          >
            <template #leading>
              <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-tint text-tint">
                <GroupIcon class="size-icon-sm" />
              </span>
            </template>
            {{ selectAllLabel }}
          </SelectionPopoverOptionRow>
          <SelectionPopoverDivider v-if="showAggregateOption" class="shrink-0" />

          <ul class="flex flex-col gap-px">
            <li v-for="student in filteredStudents" :key="student.id">
              <SelectionPopoverOptionRow
                :disabled="!props.commitOnClose && singletonAllEligibleSelection"
                highlight-offset
                indicator="checkbox"
                :selected="isSelected(student)"
                :subtle-selection="showAggregateOption && allSelected"
                @select="toggleStudent(student)"
              >
                <template #leading>
                  <UserAvatar :user="student" class="!size-8 shrink-0" />
                </template>
                <SearchTextHighlighter :text="getStudentDisplayName(student)" />
              </SelectionPopoverOptionRow>
            </li>
          </ul>
          <EmptyPlaceholder :data="filteredStudents" :message="$t('general.noResults')" />
        </div>
      </div>
    </template>
  </PositionablePopover>
</template>
